import { Button } from "@devhop/ui";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CircleCheckIcon,
  ListIcon,
} from "lucide-react";
import type {
  EventRehearsalSummaryDTO,
  RehearsalSessionDTO,
} from "../../domain/contracts";

function formatMoney(value: number) {
  return `${value.toLocaleString()} ກີບ`;
}

type EventRehearsalTrackingCardProps = {
  hasTeam: boolean;
  summary: EventRehearsalSummaryDTO | undefined;
  sessions: RehearsalSessionDTO[] | undefined;
  isLoading?: boolean;
  onOpenSchedule: () => void;
};

export function EventRehearsalTrackingCard({
  hasTeam,
  summary,
  sessions,
  isLoading,
  onOpenSchedule,
}: EventRehearsalTrackingCardProps) {
  const planned = sessions?.filter((s) => s.status === "planned").length ?? 0;
  const completed =
    sessions?.filter((s) => s.status === "completed").length ?? 0;
  const totalSessions = sessions?.length ?? 0;
  const rehearsalDays = summary?.totalRehearsalDays ?? 0;
  const rehearsalAmount = summary?.totalRehearsalAmount ?? 0;

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border bg-primary/5">
            <CalendarDaysIcon className="size-6 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
              ປະຕິທິນຊ້ອມ
            </p>
            <h3 className="mt-0.5 font-semibold text-lg tracking-tight">
              ຕິດຕາມວັນຊ້ອມ
            </h3>
            <p className="mt-1 max-w-xl text-muted-foreground text-sm leading-relaxed">
              ວາງແຜນຫຼາຍຄົນ ຢືນຢັນຫຼັງຊ້ອມ — ສະຖານະ «ສຳເລັດ» ເທົ່ານັ້ນທີ່ນັບຄ່າຕົວໃນ payout
            </p>
          </div>
        </div>

        <Button
          size="default"
          className="w-full shrink-0 sm:w-auto"
          disabled={!hasTeam}
          onClick={onOpenSchedule}
        >
          <ListIcon className="size-4" />
          ເຂົ້າລາຍການວັນຊ້ອມ
          <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      {isLoading ? (
        <div className="border-t bg-muted/20 px-5 py-3 text-muted-foreground text-sm">
          ກຳລັງໂຫຼດສະຫຼຸບ...
        </div>
      ) : !hasTeam ? (
        <div className="border-t bg-muted/20 px-5 py-3 text-muted-foreground text-sm">
          ເພີ່ມນັກສະແດງໃນທີມກ່ອນ ເພື່ອເລີ່ມຕິດຕາມວັນຊ້ອມ
        </div>
      ) : (
        <div className="grid gap-px border-t bg-border sm:grid-cols-4">
          <StatCell
            label="ລາຍການທັງໝົດ"
            value={String(totalSessions)}
            hint="ທຸກສະຖານະ"
          />
          <StatCell
            label="ວາງແຜນ"
            value={String(planned)}
            hint="ຍັງບໍ່ນັບ payout"
          />
          <StatCell
            label="ສຳເລັດ"
            value={String(completed)}
            hint="ນັບຄ່າຕົວ"
            highlight
          />
          <StatCell
            label="ຄາດຄ່າຈ້າງຊ້ອມ"
            value={rehearsalDays > 0 ? formatMoney(rehearsalAmount) : "—"}
            hint={
              rehearsalDays > 0
                ? `${rehearsalDays} ວັນຊ້ອມ (completed)`
                : "ຍັງບໍ່ມີວັນທີ່ຢືນຢັນ"
            }
            highlight={rehearsalDays > 0}
          />
        </div>
      )}
    </div>
  );
}

function StatCell({
  label,
  value,
  hint,
  highlight,
}: {
  label: string;
  value: string;
  hint: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={
        highlight
          ? "flex flex-col gap-0.5 bg-primary/5 px-4 py-3"
          : "flex flex-col gap-0.5 bg-card px-4 py-3"
      }
    >
      <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
        {highlight ? <CircleCheckIcon className="size-3 text-primary" /> : null}
        {label}
      </span>
      <span className="font-semibold text-base tabular-nums tracking-tight">
        {value}
      </span>
      <span className="text-muted-foreground text-xs">{hint}</span>
    </div>
  );
}
